import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { ClientErrorReporter } from "@masters-club/error-reporter/client";

type ErrorDetails = {
  name: string;
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  time: string;
};

type ErrorContextValue = {
  error: ErrorDetails | null;
  setError: (details: ErrorDetails) => void;
  clearError: () => void;
  reporter: ClientErrorReporter | null;
};

const ErrorContext = createContext<ErrorContextValue | null>(null);

const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!import.meta.env.DEV && envUrl && envUrl.length > 0) {
    return envUrl.replace(/\/+$/, "");
  }
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return "https://localhost:3000";
};

const buildErrorDetails = (
  error: Error,
  componentStack?: string
): ErrorDetails => ({
  name: error.name || "Error",
  message: error.message || "Неизвестная ошибка",
  stack: error.stack,
  componentStack: componentStack?.trim() || undefined,
  url: window.location.href,
  userAgent: navigator.userAgent,
  time: new Date().toISOString(),
});

const buildFallbackDetails = (message: string): ErrorDetails => ({
  name: "Error",
  message,
  url: window.location.href,
  userAgent: navigator.userAgent,
  time: new Date().toISOString(),
});

const formatReport = (details: ErrorDetails): string => {
  const lines = [
    `Time: ${details.time}`,
    `URL: ${details.url}`,
    `UserAgent: ${details.userAgent}`,
    `Name: ${details.name}`,
    `Message: ${details.message}`,
  ];

  if (details.stack) {
    lines.push("Stack:", details.stack);
  }

  if (details.componentStack) {
    lines.push("ComponentStack:", details.componentStack);
  }

  return lines.join("\n");
};

type ErrorBoundaryProps = {
  children: React.ReactNode;
  onError: (error: Error, componentStack: string | undefined) => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError(error, info.componentStack ?? undefined);
  }

  override render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

const GlobalErrorOverlay = ({
  details,
  onClose,
}: {
  details: ErrorDetails;
  onClose: () => void;
}) => {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle"
  );
  const reportText = useMemo(() => formatReport(details), [details]);

  const handleCopy = useCallback(async () => {
    if (!navigator.clipboard) {
      setCopyState("failed");
      return;
    }

    try {
      await navigator.clipboard.writeText(reportText);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      setCopyState("failed");
    }
  }, [reportText]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 px-4 py-8">
      <div className="w-full max-w-3xl rounded-xl border bg-background p-6 text-foreground shadow-2xl">
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Критическая ошибка интерфейса
            </p>
            <h1 className="text-2xl font-semibold text-foreground">
              {details.message}
            </h1>
            <p className="text-sm text-muted-foreground">{details.name}</p>
          </div>

          <div className="grid gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Страница:</span>{" "}
              <span className="break-all font-medium">{details.url}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Время:</span>{" "}
              <span className="font-medium">
                {new Date(details.time).toLocaleString("ru-RU")}
              </span>
            </div>
          </div>

          {details.stack && (
            <div>
              <p className="mb-2 text-sm font-medium">Stack trace</p>
              <pre className="max-h-60 overflow-auto rounded-lg border bg-muted/40 p-3 text-xs leading-relaxed text-foreground">
                {details.stack}
              </pre>
            </div>
          )}

          {details.componentStack && (
            <div>
              <p className="mb-2 text-sm font-medium">Component stack</p>
              <pre className="max-h-60 overflow-auto rounded-lg border bg-muted/40 p-3 text-xs leading-relaxed text-foreground">
                {details.componentStack}
              </pre>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={handleCopy}>
              {copyState === "copied"
                ? "Скопировано"
                : (copyState === "failed"
                ? "Не удалось скопировать"
                : "Скопировать данные")}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Закрыть
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const GlobalErrorProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [error, setErrorState] = useState<ErrorDetails | null>(null);

  // Один инстанс reporter на всё приложение — держим в ref, чтобы
  // не пересоздавать при перерендерах и не ловить повторные attach глобальных хендлеров.
  const reporterRef = useRef<ClientErrorReporter | null>(null);
  if (reporterRef.current === null) {
    reporterRef.current = new ClientErrorReporter({
      endpoint: `${getApiUrl()}/api/error-reports`,
      appName: "admin",
      environment: import.meta.env.MODE ?? "production",
    });
  }

  const setError = useCallback((details: ErrorDetails) => {
    setErrorState(details);
  }, []);

  const clearError = useCallback(() => {
    setErrorState(null);
  }, []);

  useEffect(() => {
    const reporter = reporterRef.current;
    if (!reporter) {
      return;
    }

    // Подключаем window.onerror, unhandledrejection, fetch interceptor, console.error tracking.
    reporter.attachGlobalHandlers();

    const handleError = (event: ErrorEvent) => {
      if (event.error instanceof Error) {
        setError(buildErrorDetails(event.error));
        return;
      }
      const fallbackMessage = event.message || "Неизвестная ошибка";
      setError(buildFallbackDetails(fallbackMessage));
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      if (event.reason instanceof Error) {
        setError(buildErrorDetails(event.reason));
        return;
      }
      const reasonText =
        typeof event.reason === "string"
          ? event.reason
          : "Необработанное отклонение Promise";
      setError(buildFallbackDetails(reasonText));
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, [setError]);

  const contextValue = useMemo<ErrorContextValue>(
    () => ({
      error,
      setError,
      clearError,
      reporter: reporterRef.current,
    }),
    [error, setError, clearError]
  );

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
      {error && <GlobalErrorOverlay details={error} onClose={clearError} />}
    </ErrorContext.Provider>
  );
};

export const GlobalErrorBoundary = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const context = useContext(ErrorContext);

  const handleError = useCallback(
    (error: Error, componentStack: string | undefined) => {
      if (!context) {
        return;
      }
      // Сначала репортим на backend, потом показываем модалку.
      void context.reporter?.report(error, { componentStack });
      context.setError(buildErrorDetails(error, componentStack));
    },
    [context]
  );

  if (!context) {
    return children;
  }

  return <ErrorBoundary onError={handleError}>{children}</ErrorBoundary>;
};
